import React, { useState, useRef, useEffect } from "react";
import BrandLogo from "./brand-logo";
import DesktopNavigation from "./desktop-navigation";
import UserMenuDropdown from "./user-menu-dropdown";
import ThemeToggle from "./theme-toggle";
import MobileMenu from "./mobile-menu";
import { NavigationItemData } from "./mobile-menu";
import { User } from "../../types/auth";

interface AppHeaderProps {
  navigation: NavigationItemData[];
  user: User | null;
  isDarkMode: boolean;
  onThemeToggle: () => void;
  onLogout: () => void;
}

/**
 * Main application header component.
 * Orchestrates logo, navigation, user menu, and theme toggle.
 * Handles both desktop and mobile layouts.
 */
const AppHeader: React.FC<AppHeaderProps> = ({
  navigation,
  user,
  isDarkMode,
  onThemeToggle,
  onLogout,
}) => {
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Toggle submenu (mobile accordion style)
  const toggleMenu = (menuName: string): void => {
    setOpenMenus((prev) =>
      prev.includes(menuName) ? prev.filter((name) => name !== menuName) : [menuName]
    );
  };

  // Show submenu on hover (desktop)
  const showMenu = (menuName: string): void => {
    setOpenMenus([menuName]);
  };

  // Hide all submenus
  const hideMenu = (): void => {
    setOpenMenus([]);
  };

  // Toggle mobile menu
  const toggleMobileMenu = (): void => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setOpenMenus([]);
  };

  // Close mobile menu
  const closeMobileMenu = (): void => {
    setIsMobileMenuOpen(false);
    setOpenMenus([]);
  };

  // Close dropdowns when clicking outside header
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent): void => {
      const target = event.target as Element;
      if (isMobileMenuOpen && headerRef.current != null && !headerRef.current.contains(target)) {
        closeMobileMenu();
      }
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-40 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <BrandLogo />

          {/* Desktop Navigation */}
          <DesktopNavigation
            navigation={navigation}
            openMenus={openMenus}
            onShowMenu={showMenu}
            onHideMenu={hideMenu}
            onSubItemClick={hideMenu}
          />

          {/* Right side actions - tighter spacing on mobile */}
          <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3">
            {/* User Menu Dropdown */}
            <UserMenuDropdown
              user={user}
              isOpen={isUserMenuOpen}
              onToggle={() => setIsUserMenuOpen(!isUserMenuOpen)}
              onClose={() => setIsUserMenuOpen(false)}
              onLogout={onLogout}
            />

            {/* Theme Toggle - hidden on mobile (available in mobile menu) */}
            <ThemeToggle
              isDarkMode={isDarkMode}
              onToggle={onThemeToggle}
              className="hidden sm:flex border border-gray-300 dark:border-gray-600"
            />

            {/* Mobile menu button */}
            <div className="xl:hidden">
              <button
                onClick={toggleMobileMenu}
                className="flex items-center justify-center min-w-[44px] min-h-[44px] p-2.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700 dark:border-gray-600 transition-colors"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        navigation={navigation}
        openMenus={openMenus}
        onToggleMenu={toggleMenu}
        onClose={closeMobileMenu}
        isDarkMode={isDarkMode}
        onThemeToggle={onThemeToggle}
      />
    </header>
  );
};

export default AppHeader;
